import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from "react-router-dom";
import {useFormik} from "formik";
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack,
    IconButton, Tab, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import {AltRoute, ChevronRight, PlayArrow} from "@mui/icons-material";
import BetaBadge from "./layout/BetaBadge";
import Constants from "../../utils/constants";
import history from "../../utils/history";
import {toastHelper} from "../../utils/toast-helper";
import './UsersNewOrEdit.css';
import {
    useCreateSimulationMutation, useEnregistrerSimulationMutation, useGetAgencesSimulationQuery, useGetCalquesCollecteQuery,
    useGetCalquesTourneesQuery, useGetJourneeQuery, useGetSensibiliteQuery, useGetSimulationQuery,
} from "../../modules/simulations/simulations.api";
import {useGetPatrimoinesLightByClientQuery} from "../../modules/patrimoines/patrimoines.api";
import {
    appliquerCalendrier, parseFiger, pollingSimulation, valeursInitiales, valeursOuverture,
} from "./simulations/simulationRules";
import {
    affectationsDuPav, afficheeALOuverture, calqueVisible, calquesCollecte, calquesTournees, doitConfirmerSortie, enregistrerScenario,
    etatBoutonJouer, fichePav, figerDansVolet, jouerScenario, libelleProposeEnregistrement, libelleValide, paramsUrl,
    lignesNonAffectees, peutEnregistrer, pointsNonAffectes, raisonDeLaVisite, sectionsEnErreur, verdictSimulation,
} from "./simulations/atelierRules";
import {
    apresCran, apresJouer, choisirJournee, cleJournee, ecrireVoletReplie, etatVideOnglet, lireVoletReplie, ONGLET_DEFAUT, ONGLETS,
    positionsDeLaJournee, reglerGroupe,
} from "./simulations/lot9Rules";
import TableJournees from "./simulations/TableJournees";
import {raisonsGenerales} from "./simulations/simulationRules";
import {schemaAtelier} from "./simulations/simulationSchema";
import {useEtatCalendrier} from "./simulations/useEtatCalendrier";
import AtelierVolet from "./simulations/AtelierVolet";
import AtelierCarte from "./simulations/AtelierCarte";
import AtelierLegende from "./simulations/AtelierLegende";
import AtelierRail from "./simulations/AtelierRail";
import VerdictSimulation from "./simulations/VerdictSimulation";
import BoutonExporter from "./simulations/BoutonExporter";
import BadgeImporte from "./simulations/BadgeImporte";
import {rejouerDe, texteSnapshot} from "./simulations/echangeScenarioRules";
import {mentionRecap} from "./simulations/calibrationRules";
import BlocJournee from "./simulations/BlocJournee";
import PiluleDistances from "./simulations/PiluleDistances";
import RecapCampagne from "./simulations/RecapCampagne";
import PiluleErreur from "./simulations/PiluleErreur";
import BlocSensibilite from "./simulations/BlocSensibilite";
import {appliquerCran, appliquerCranAuVolet, doitPreRemplir, cleOuverture, ligneDeCran} from "./simulations/sensibiliteRules";
import {CARD_SX} from "./interventions/v2/primitives.js";

const FOND = '#F6F8F4';

/** Onglet sans contenu : un message nommé dans une carte (GUIDE_UI : carte radius 16, pas de bloc vide muet). */
function EtatVide({texte}) {
    return (
        <Box sx={{...CARD_SX, p: 3, textAlign: 'center'}} data-testid="etat-vide">
            <Typography sx={{color: '#8A929C', fontWeight: 600}}>{texte}</Typography>
        </Box>
    );
}

/**
 * Atelier sur carte (TOUR-03 lot 4, U-TOUR-03-1) — floorplan Worklist plein écran, classe Bureau.
 * `?patrimoine=&client=&from=&figer=` : `from` pré-remplit le volet depuis \`parametres\` du scénario source et affiche ses calques.
 * Jouer = POST d'un brouillon ; on reste dans l'atelier et on suit le calcul par polling 5 s sur \`GET /simulations/{id}\`.
 */
function SimulationAtelierPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const patrimoineId = searchParams.get('patrimoine');
    const clientId = searchParams.get('client');
    const fromId = searchParams.get('from');
    const figer = useMemo(() => parseFiger(searchParams.get('figer')), [searchParams]);
    const cranParam = searchParams.get('cran'); // lot 7 : cran de la sensibilité du scénario `from`, appliqué au volet sans jouer

    const [tentee, setTentee] = useState(false);
    const [erreursGlobales, setErreursGlobales] = useState([]);
    const [ouvertes, setOuvertes] = useState(['periode', 'equipes']);
    const [afficheId, setAfficheId] = useState(afficheeALOuverture(fromId));
    const [choixCalques, setChoixCalques] = useState({});
    const [avecTraces, setAvecTraces] = useState(false);
    const [selection, setSelection] = useState(null);
    const [journeeActive, setJourneeActive] = useState(null);
    const [calibreJoue, setCalibreJoue] = useState(null); // « curseurs calibrés (n journées) » figé au moment de Jouer, avec le scénario affiché
    const [enregistrementOuvert, setEnregistrementOuvert] = useState(false);
    const [libelleEnreg, setLibelleEnreg] = useState('');
    const [erreurEnreg, setErreurEnreg] = useState(null);
    const [sortieOuverte, setSortieOuverte] = useState(false);
    const [recapOuvert, setRecapOuvert] = useState(false);
    const [voletReplie, setVoletReplie] = useState(() => lireVoletReplie(window.localStorage)); // lot 9 : mémorisé
    const [onglet, setOnglet] = useState(ONGLET_DEFAUT);
    const [detailCle, setDetailCle] = useState(null); // lot 9 : ligne dépliée de l'onglet Journées (une seule à la fois)
    const [focus, setFocus] = useState(null); // lot 9 : journée sur laquelle recentrer la carte {positions, nonce}
    const replier = (valeur) => {
        setVoletReplie(valeur);
        ecrireVoletReplie(window.localStorage, valeur);
    };
    const initialise = useRef(null);
    const sortieEnAttente = useRef(null);
    const debloquer = useRef(null);

    const {data: patrimoines = []} = useGetPatrimoinesLightByClientQuery(clientId, {skip: !clientId});
    const patrimoine = patrimoines.find((p) => String(p.id) === String(patrimoineId));
    const {data: agences = [], isLoading: agencesLoading} = useGetAgencesSimulationQuery();
    const {data: source, error: sourceError} = useGetSimulationQuery(fromId, {skip: !fromId});
    const {data: sensibiliteSource, error: sensibiliteSourceError} = useGetSensibiliteQuery(fromId, {skip: !fromId || !cranParam});
    const {loading: calendrierLoading, erreur: calendrierErreur, present: aCalendrier} = useEtatCalendrier(patrimoineId);

    const [polling, setPolling] = useState(0);
    // currentData : jamais l'ancien brouillon pendant que le nouveau se charge (Enregistrer, calques).
    const {currentData: affichee, error: erreurAffichee} = useGetSimulationQuery(afficheId, {skip: !afficheId, pollingInterval: polling});
    useEffect(() => {
        setPolling(pollingSimulation(affichee));
    }, [affichee]);

    const libellePatrimoine = patrimoine?.libelle ?? source?.patrimoine?.libelle ?? affichee?.patrimoine?.libelle ?? null;

    // Calques : collecte (sans calcul) dès l'ouverture ; tournées du scénario affiché quand il est `done` (409 sinon).
    const {data: donneesCollecte} = useGetCalquesCollecteQuery(patrimoineId, {skip: !patrimoineId});
    const {currentData: donneesTournees, error: erreurCalques} = useGetCalquesTourneesQuery(afficheId, {skip: !afficheId || affichee?.statut !== 'done'});
    const calquesC = useMemo(() => calquesCollecte(donneesCollecte), [donneesCollecte]);
    const calquesT = useMemo(() => calquesTournees(donneesTournees), [donneesTournees]);
    const nonAffectes = useMemo(() => pointsNonAffectes(donneesTournees), [donneesTournees]);
    const aTournees = calquesT.length > 0;
    const nbBacsPatrimoine = useMemo(() => (donneesCollecte?.pavs ?? []).reduce((n, p) => n + Number(p.quantite ?? 0), 0), [donneesCollecte]);
    const visible = useCallback((cle) => calqueVisible(cle, choixCalques, {aTournees}), [choixCalques, aTournees]);
    const regler = (cles, valeur) => setChoixCalques((c) => reglerGroupe(c, cles, valeur));
    const basculer = (cle) => setChoixCalques((c) => ({...c, [cle]: !calqueVisible(cle, c, {aTournees})}));
    const selectionnerPav = useCallback((nomenclature, journee) => {
        setSelection(nomenclature);
        if (journee) setJourneeActive(journee);
    }, []);

    const [createSimulation, {isLoading: envoi}] = useCreateSimulationMutation();
    const [enregistrer, {isLoading: enregistrement}] = useEnregistrerSimulationMutation();

    const formik = useFormik({
        initialValues: valeursInitiales(),
        validationSchema: schemaAtelier,
        validateOnMount: false,
        onSubmit: async (values) => {
            setErreursGlobales([]);
            try {
                const r = await jouerScenario((payload) => createSimulation(payload).unwrap(), values, patrimoineId, new Date(), rejouerDe(source));
                if (r.cree) {
                    setAfficheId(String(r.cree.id));
                    setCalibreJoue(mentionRecap(values));
                    setSelection(null);
                    setJourneeActive(null);
                    setChoixCalques({});
                    setFocus(null);
                    setDetailCle(null);
                    setJourneeEnAttente(null);
                    setOnglet(apresJouer({onglet}).onglet);
                    return;
                }
                formik.setErrors(r.erreurs);
                setErreursGlobales(r.globales);
                setOuvertes((o) => [...new Set([...o, ...sectionsEnErreur(r.erreurs)])]);
                replier(false); // jouer depuis la poignée : les erreurs se lisent dans le volet
            } catch (e) {
                toastHelper.handleError(e, "Oops, une erreur est survenue.");
            }
        },
    });

    // Pré-remplissage : `?from=<id>` (Dupliquer / Figer ce jour), sinon vierge ; le calendrier fixe le défaut de « contraint ».
    useEffect(() => {
        if ((fromId && !source && !sourceError) || calendrierLoading) return;
        if (fromId && cranParam && !sensibiliteSource && !sensibiliteSourceError) return;
        if (!doitPreRemplir(initialise.current, fromId, cranParam)) return; // une seule fois par scénario source / cran : un rafraîchissement du cache n'écrase pas le volet
        initialise.current = cleOuverture(fromId, cranParam);
        const valeurs = valeursOuverture(fromId && source ? source : null, figer);
        const base = fromId && source ? valeurs : {...valeurs, contraint: aCalendrier};
        const cran = fromId && source ? ligneDeCran(sensibiliteSource, cranParam) : null;
        if (fromId && cranParam && source && !cran) toastHelper.handleError(null, 'Cran introuvable dans l’analyse de sensibilité : le volet reprend le scénario source.');
        formik.resetForm({values: appliquerCalendrier(cran ? appliquerCran(base, cran) : base, aCalendrier)});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source, sourceError, fromId, figer, calendrierLoading, aCalendrier, sensibiliteSource, sensibiliteSourceError, cranParam]);

    // Rail de contexte : fiche du PAV cliqué ; la raison d'une visite vient du détail de sa journée.
    const affectationsSel = useMemo(() => affectationsDuPav(calquesT, selection), [calquesT, selection]);
    const dateSel = affectationsSel[0]?.date ?? null;
    const {data: detailJournee} = useGetJourneeQuery({id: afficheId, date: dateSel}, {skip: !selection || !dateSel || !afficheId});
    const fiche = useMemo(() => fichePav({
        nomenclature: selection, collecte: donneesCollecte, calques: calquesT, nonAffectees: donneesTournees?.nonAffectees,
        raisonVisite: raisonDeLaVisite(detailJournee, selection, affectationsSel[0]?.ordre),
    }), [selection, donneesCollecte, calquesT, donneesTournees, detailJournee, affectationsSel]);
    const positionSel = useMemo(() => {
        const p = (donneesCollecte?.pavs || []).find((x) => x.nomenclature === selection);
        if (p) return [p.lat, p.lon];
        return calquesT.flatMap((c) => c.points).find((x) => x.nomenclature === selection)?.position ?? null;
    }, [donneesCollecte, selection, calquesT]);

    // Lot 9 : clic sur une journée de l'onglet Journées → onglet Carte, centrée sur cette journée, son calque seul.
    const appliquerJournee = (cle) => {
        const suite = choisirJournee({onglet}, calquesT, cle, choixCalques);
        setOnglet(suite.onglet);
        setJourneeActive(suite.journeeActive);
        setChoixCalques(suite.choixCalques);
        setFocus({positions: positionsDeLaJournee(calquesT, cle), nonce: Date.now()});
    };
    // Calques tournées pas encore arrivés : le clic est gardé, calque seul et recentrage s'appliquent à leur arrivée.
    const [journeeEnAttente, setJourneeEnAttente] = useState(null);
    const ouvrirJourneeSurCarte = (j) => {
        const cle = cleJournee(j);
        if (calquesT.length === 0) {
            setOnglet('carte');
            setJourneeActive(cle);
            if (!erreurCalques) setJourneeEnAttente(cle);
            return;
        }
        appliquerJournee(cle);
    };
    // Lecture des calques en échec : le clic gardé ne sera jamais appliqué (alerte nommée affichée dans la zone de droite).
    useEffect(() => {
        if (erreurCalques) setJourneeEnAttente(null);
    }, [erreurCalques]);
    useEffect(() => {
        if (!journeeEnAttente || calquesT.length === 0) return;
        setJourneeEnAttente(null);
        appliquerJournee(journeeEnAttente);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [journeeEnAttente, calquesT]);

    const figerCeJour = (nomenclature, date) => {
        formik.setValues(figerDansVolet(formik.values, {nomenclature, date}));
        setOuvertes((o) => [...new Set([...o, 'verrous'])]);
        replier(false); // le verrou ajouté se voit dans le volet
        toastHelper.displaySuccess(`${nomenclature} figé le ${date} : rejouez pour en tenir compte.`);
    };

    // Lot 7 : « Appliquer ce cran » remplit le volet avec les `parametres` de la ligne ; rien n'est joué ni enregistré.
    const appliquerUnCran = (ligne) => {
        appliquerCranAuVolet(formik.setValues, formik.values, ligne, aCalendrier);
        setOuvertes((o) => [...new Set([...o, 'periode', 'equipes'])]);
        replier(apresCran({voletReplie}).voletReplie); // le volet se rouvre : la valeur modifiée se voit
        toastHelper.displaySuccess(`Cran appliqué au volet : ${ligne.libelle}. Cliquez sur Jouer pour le calculer.`);
    };

    const etatAttendu = {attendu: Boolean(afficheId), erreur: Boolean(erreurAffichee)};
    const verdict = useMemo(() => verdictSimulation(affichee), [affichee]);
    const echec = affichee?.statut === 'failed';
    const ouvrirEnregistrement = () => {
        setLibelleEnreg(libelleProposeEnregistrement(formik.values, affichee));
        setErreurEnreg(null);
        setEnregistrementOuvert(true);
    };
    const confirmerEnregistrement = async () => {
        if (!libelleValide(libelleEnreg)) {
            setErreurEnreg('Libellé : 1 à 120 caractères.');
            return;
        }
        try {
            await enregistrerScenario((arg) => enregistrer(arg).unwrap(), affichee.id, libelleEnreg);
            toastHelper.displaySuccess("Scénario enregistré : il rejoint la table de comparaison.");
            setEnregistrementOuvert(false);
        } catch (e) {
            if (e?.status === 422) setErreurEnreg(e.data?.violations?.[0]?.message ?? 'Libellé invalide.');
            else if (e?.status === 409) {
                setEnregistrementOuvert(false);
                toastHelper.handleError(e, "Le calcul n'est pas terminé.");
            } else toastHelper.handleError(e, "Oops, une erreur est survenue.");
        }
    };

    const confirmerSortie = doitConfirmerSortie(affichee);
    // Toute navigation (menu, retour du navigateur, bouton Retour) est suspendue tant qu'un brouillon est à perdre ;
    // history v5 pose aussi son propre beforeunload pour la fermeture de l'onglet.
    useEffect(() => {
        if (!confirmerSortie) return undefined;
        const lever = history.block((tx) => {
            sortieEnAttente.current = tx;
            setSortieOuverte(true);
        });
        debloquer.current = lever;
        return () => {
            lever();
            debloquer.current = null;
        };
    }, [confirmerSortie]);
    const quitter = () => {
        const tx = sortieEnAttente.current;
        sortieEnAttente.current = null;
        debloquer.current?.();
        setSortieOuverte(false);
        if (tx) tx.retry(); else navigate(retour);
    };

    const sectionsFautives = useMemo(() => sectionsEnErreur(formik.errors), [formik.errors]);

    const toggleSection = (id) => setOuvertes((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));

    const jouer = async () => {
        setTentee(true);
        const errs = await formik.validateForm();
        if (Object.keys(errs).length > 0) {
            setOuvertes((o) => [...new Set([...o, ...sectionsEnErreur(errs)])]);
            replier(false);
            return;
        }
        formik.handleSubmit();
    };

    const bouton = etatBoutonJouer({envoi, simulation: affichee, peut: true});
    const retour = Constants.APP_ROUTE_SIMULATIONS + paramsUrl({client: clientId, patrimoine: patrimoineId});

    if (!patrimoineId) {
        return (
            <Stack spacing={2}>
                <Alert severity="info">Choisissez d'abord un client et un patrimoine depuis la liste des simulations.</Alert>
                <div><Button color="primary" onClick={() => navigate(Constants.APP_ROUTE_SIMULATIONS)}>Retour aux simulations</Button></div>
            </Stack>
        );
    }

    const pied = (
        <Button fullWidth variant="contained" onClick={jouer} disabled={bouton.disabled}
                startIcon={bouton.calcul ? <CircularProgress size={18} color="inherit"/> : null}
                sx={{
                    borderRadius: 999, py: 1.1, fontWeight: 700, textTransform: 'none', fontSize: 15,
                    bgcolor: '#213067', color: '#fff', '&:hover': {bgcolor: '#0F2D69'},
                    '&.Mui-disabled': {bgcolor: '#E3E7EC', color: '#8A929C'},
                }}>
            {bouton.label}
        </Button>
    );

    return (
        <Box sx={{bgcolor: echec ? '#FBF4F4' : FOND, m: '-1rem', p: 2, height: 'calc(100vh - var(--topbar-height) - .5rem)', boxSizing: 'border-box',
            display: 'flex', flexDirection: 'column', gap: 2}} data-testid="atelier">
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{flex: 'none'}}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{minWidth: 0}}>
                    <AltRoute sx={{color: '#213067'}}/>
                    <Typography component="h1" sx={{fontSize: 20, fontWeight: 800, color: '#213067'}} noWrap>
                        Atelier de découpage{libellePatrimoine ? ` · ${libellePatrimoine}` : ''}
                    </Typography>
                    <BetaBadge/>
                    <BadgeImporte simulation={affichee ?? source}/>
                </Stack>
                <Button color="primary" onClick={() => navigate(retour)}>Retour aux scénarios</Button>
            </Stack>

            {fromId && source && texteSnapshot(source) &&
                <Typography variant="body2" data-testid="snapshot" sx={{flex: 'none', color: '#213067', fontWeight: 600}}>{texteSnapshot(source)}</Typography>}

            {fromId && sourceError &&
                <Alert severity="error" sx={{flex: 'none'}}>Scénario source introuvable : le volet est vierge.</Alert>}

            <Box sx={{flex: '1 1 auto', minHeight: 0, display: 'grid', gridTemplateColumns: {xs: '1fr', md: `${voletReplie ? '40px' : '360px'} minmax(0, 1fr)`}, gap: 2}}>
                {voletReplie &&
                    <Box sx={{...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1, minHeight: 0}} data-testid="poignee-volet">
                        <Tooltip title="Déplier le volet" placement="right">
                            <IconButton size="small" aria-label="Déplier le volet" onClick={() => replier(false)} data-testid="deplier-volet"><ChevronRight/></IconButton>
                        </Tooltip>
                        <Tooltip title={bouton.label} placement="right">
                            <span>
                                <IconButton size="small" aria-label="Jouer" onClick={jouer} disabled={bouton.disabled} data-testid="jouer-poignee"
                                            sx={{bgcolor: '#213067', color: '#fff', '&:hover': {bgcolor: '#0F2D69'}, '&.Mui-disabled': {bgcolor: '#E3E7EC', color: '#8A929C'}}}>
                                    {bouton.calcul ? <CircularProgress size={18} color="inherit"/> : <PlayArrow/>}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>}
                <Box sx={{display: voletReplie ? 'none' : 'block', minHeight: 0, height: '100%'}}>
                <AtelierVolet onReplier={() => replier(true)} formik={formik} tentee={tentee} patrimoine={patrimoineId} agences={agences} agencesLoading={agencesLoading}
                              aCalendrier={aCalendrier} calendrierLoading={calendrierLoading} calendrierErreur={calendrierErreur}
                              ouvertes={ouvertes} onToggle={toggleSection} sectionsFautives={sectionsFautives}
                              erreursGlobales={erreursGlobales} pied={pied}/>
                </Box>
                <Box sx={{minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2}} data-testid="atelier-droite">
                    <VerdictSimulation verdict={verdict}
                        sousLigne={verdict && affichee ? [affichee.libelle, affichee.enregistre ? 'enregistré' : 'brouillon',
                            verdict.nonAffectees > 0 ? `${verdict.nonAffectees} visites non affectées` : null,
                            calibreJoue].filter(Boolean).join(' · ') : null}
                        details={affichee?.statut === 'done' ? lignesNonAffectees(affichee) : []}
                        pilules={affichee?.statut === 'done' && (<>
                            <PiluleDistances courte distances={affichee.totaux?.distances}/>
                            <PiluleErreur courte erreurMesuree={affichee.erreurMesuree} patrimoineId={patrimoineId}/>
                        </>)}
                        actions={affichee?.statut === 'done' && (<>
                            <Button variant="outlined" onClick={() => setRecapOuvert(true)} sx={{borderRadius: 999, textTransform: 'none', fontWeight: 700}}>Récap</Button>
                            <BoutonExporter simulation={affichee}/>
                            {affichee.enregistre
                                ? <Typography sx={{fontWeight: 700, color: '#5C8316'}}>Enregistré</Typography>
                                : <Button variant="contained" disabled={!peutEnregistrer(affichee)} onClick={ouvrirEnregistrement}
                                          sx={{borderRadius: 999, textTransform: 'none', fontWeight: 700, bgcolor: '#87BC26', color: '#fff', '&:hover': {bgcolor: '#5C8316'}}}>
                                    Enregistrer
                                </Button>}</>)}/>
                    {erreurCalques && affichee?.statut === 'done' &&
                        <Alert severity="error" data-testid="erreur-calques" sx={{flex: 'none'}}>
                            Impossible de charger les journées du scénario sur la carte : rechargez la page.
                        </Alert>}
                    <Tabs value={onglet} onChange={(_e, v) => { setJourneeEnAttente(null); setOnglet(v); }} aria-label="onglets de l'atelier" data-testid="onglets-atelier"
                          sx={{flex: 'none', minHeight: 40, '& .MuiTab-root': {minHeight: 40, textTransform: 'none', fontWeight: 700}}}>
                        {ONGLETS.map((o) => <Tab key={o.id} value={o.id} label={o.libelle} data-testid={`onglet-${o.id}`}/>)}
                    </Tabs>
                    <Box sx={{flex: '1 1 auto', minHeight: 0, display: onglet === 'carte' ? 'flex' : 'none', flexDirection: 'column', gap: 2}}
                         role="tabpanel" data-testid="panneau-carte">
                        <Box sx={{flex: '1 1 auto', minHeight: 0, display: 'flex', flexWrap: {xs: 'wrap', lg: 'nowrap'}, gap: 2}}>
                            <Box sx={{...CARD_SX, position: 'relative', flex: '1 1 0', minWidth: 0, minHeight: 320}} data-testid="carte-atelier">
                                <AtelierCarte pavs={donneesCollecte?.pavs} calquesCollecte={calquesC} calquesTournees={calquesT}
                                              nonAffectes={nonAffectes} visible={visible} avecTraces={avecTraces && onglet === 'carte'}
                                              selection={selection} journeeActive={journeeActive}
                                              onSelectPav={selectionnerPav}
                                              simulationId={affichee?.statut === 'done' ? affichee.id : null}
                                              cadrageCle={`${patrimoineId}|${aTournees ? afficheId : ''}`} focus={focus}/>
                                <AtelierLegende nbPav={donneesCollecte?.pavs?.length ?? 0} nbBacs={nbBacsPatrimoine} calquesCollecte={calquesC} calquesTournees={calquesT}
                                                nbNonAffectes={donneesTournees?.nonAffectees?.length ?? 0}
                                                simulationId={affichee?.statut === 'done' ? affichee.id : null}
                                                visible={visible} onToggle={basculer} avecTraces={avecTraces && onglet === 'carte'}
                                                onToggleTraces={() => setAvecTraces((t) => !t)}
                                                journeeActive={journeeActive} onSelectJournee={(cle) => setJourneeActive((a) => (a === cle ? null : cle))}
                                                onGroupe={regler}/>
                                {journeeActive && affichee?.statut === 'done' &&
                                    <Button size="small" variant="outlined" data-testid="voir-detail" onClick={() => { setJourneeEnAttente(null); setDetailCle(journeeActive); setOnglet('journees'); }}
                                            sx={{position: 'absolute', left: 12, bottom: 24, zIndex: 1000, bgcolor: '#fff', borderRadius: 999, textTransform: 'none', fontWeight: 700, '&:hover': {bgcolor: '#F6F8F4'}}}>
                                        Voir le détail
                                    </Button>}
                            </Box>
                            {fiche && <AtelierRail fiche={fiche} position={positionSel} onFermer={() => setSelection(null)} onFiger={figerCeJour}/>}
                        </Box>

                    </Box>
                    {onglet === 'journees' &&
                        <Box sx={{flex: '1 1 auto', minHeight: 0, overflowY: 'auto'}} role="tabpanel" data-testid="panneau-journees">
                            {etatVideOnglet('journees', affichee, etatAttendu)
                                ? <EtatVide texte={etatVideOnglet('journees', affichee, etatAttendu)}/>
                                : <Box sx={{...CARD_SX, p: 1.5}}>
                                    {raisonsGenerales(affichee.raisons).map((r, k) => <Typography key={k} variant="caption" sx={{display: 'block'}}>{r}</Typography>)}
                                    <Typography variant="caption" sx={{display: 'block', mb: 1, color: '#8A929C'}}>Cliquez sur une journée pour la voir sur la carte.</Typography>
                                    <TableJournees simulation={affichee} onSelect={ouvrirJourneeSurCarte} estSelectionnee={(j) => journeeActive === cleJournee(j)}
                                                   onDetail={(j) => setDetailCle((d) => (d === cleJournee(j) ? null : cleJournee(j)))}
                                                   detailOuvert={(j) => detailCle === cleJournee(j)}
                                                   renderDetail={(j) => <BlocJournee simulation={affichee} date={j.date} avecCarte={false} onFiger={figerCeJour}/>}/>
                                </Box>}
                        </Box>}
                    {onglet === 'sensibilite' &&
                        <Box sx={{flex: '1 1 auto', minHeight: 0, overflowY: 'auto'}} role="tabpanel" data-testid="panneau-sensibilite">
                            {etatVideOnglet('sensibilite', affichee, etatAttendu)
                                ? <EtatVide texte={etatVideOnglet('sensibilite', affichee, etatAttendu)}/>
                                : <BlocSensibilite simulation={affichee} onAppliquer={appliquerUnCran}/>}
                        </Box>}
                </Box>
            </Box>

            <Dialog open={recapOuvert && affichee?.statut === 'done'} onClose={() => setRecapOuvert(false)} fullWidth maxWidth="lg">
                <DialogTitle>Récap de campagne</DialogTitle>
                <DialogContent><RecapCampagne simulationId={affichee?.id} onRejouer={() => { setRecapOuvert(false); navigate(Constants.APP_ROUTE_SIMULATIONS_ATELIER + paramsUrl({client: clientId, patrimoine: patrimoineId, from: affichee.id})); }}/></DialogContent>
                <DialogActions><Button color="primary" onClick={() => setRecapOuvert(false)}>Fermer</Button></DialogActions>
            </Dialog>
            <Dialog open={enregistrementOuvert} onClose={() => setEnregistrementOuvert(false)} fullWidth maxWidth="xs">
                <DialogTitle>Enregistrer le scénario</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth size="small" label="Libellé" value={libelleEnreg} sx={{mt: 1}}
                               onChange={(e) => setLibelleEnreg(e.target.value)} error={Boolean(erreurEnreg)} helperText={erreurEnreg}/>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setEnregistrementOuvert(false)}>Annuler</Button>
                    <Button color="secondary" variant="contained" sx={{color: 'white'}} disabled={enregistrement} onClick={confirmerEnregistrement}>Enregistrer</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={sortieOuverte} onClose={() => { sortieEnAttente.current = null; setSortieOuverte(false); }}>
                <DialogTitle>Quitter l'atelier ?</DialogTitle>
                <DialogContent><DialogContentText>Ce brouillon n'est pas enregistré : il sera purgé.</DialogContentText></DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => { sortieEnAttente.current = null; setSortieOuverte(false); }}>Rester</Button>
                    <Button color="secondary" variant="contained" sx={{color: 'white'}} onClick={quitter}>Quitter</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SimulationAtelierPage;
